import React from "react";
import { Shield, Lock, Globe, Key, FileCheck, Eye } from "lucide-react";
import { Section, Button, CheckItem } from "@vild/site-ui";

const FeatureBlock = ({ icon: Icon, title, description, children }: any) => (
  <div className="bg-zinc-950 border border-zinc-800 p-8 rounded-xl flex flex-col h-full hover:border-zinc-700 transition-colors">
    <div className="w-12 h-12 rounded-lg bg-zinc-900 border border-zinc-800 flex items-center justify-center mb-6 text-zinc-300">
      <Icon className="w-6 h-6" />
    </div>
    <h3 className="text-xl font-bold text-white mb-2">{title}</h3>
    <p className="text-zinc-400 text-sm mb-6 flex-1">{description}</p>
    <div className="mt-auto pt-6 border-t border-zinc-900">{children}</div>
  </div>
);

export const Enterprise: React.FC = () => {
  return (
    <div className="bg-black min-h-screen">
      {/* HERO */}
      <div className="relative pt-40 pb-20 border-b border-zinc-900 overflow-hidden">
        <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-blue-900/10 rounded-full blur-[120px] pointer-events-none"></div>

        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center relative z-10">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded bg-blue-900/20 border border-blue-900/30 text-blue-400 text-xs font-mono mb-8">
              ENTERPRISE PLATFORM
            </div>
            <h1 className="text-5xl md:text-7xl font-bold mb-6 text-white tracking-tight">
              Scale{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-teal-400">
                Securely.
              </span>
            </h1>
            <p className="text-xl text-zinc-500 mb-10 max-w-lg leading-relaxed">
              Infrastructure for the modern media stack. SSO, audit logs, and private runners for
              organizations that demand control.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button size="lg" variant="inverse" asLink="/signup">
                Start Free Trial
              </Button>
              <Button size="lg" variant="outline" asLink="mailto:sales@vild.ai">
                Talk to Sales
              </Button>
            </div>

            <div className="mt-12 flex items-center gap-6 opacity-40 grayscale hover:grayscale-0 transition-all">
              <div className="text-xs font-bold border border-white px-2 py-1 text-white rounded">SOC 2 Type II</div>
              <div className="text-xs font-bold border border-white px-2 py-1 text-white rounded">GDPR Ready</div>
              <div className="text-xs font-bold border border-white px-2 py-1 text-white rounded">ISO 27001</div>
            </div>
          </div>

          <div className="relative">
            <div className="rounded-2xl bg-zinc-900 border border-zinc-800 p-2 shadow-2xl rotate-1">
              <div className="bg-black rounded-xl border border-zinc-800 p-6 overflow-hidden relative">
                <div className="flex justify-between items-center border-b border-zinc-900 pb-4 mb-4">
                  <div className="flex items-center gap-2 text-zinc-400">
                    <Lock className="w-4 h-4" /> <span className="text-sm font-mono">Security Overview</span>
                  </div>
                  <div className="flex gap-1">
                    <div className="w-2 h-2 rounded-full bg-green-500"></div>
                    <div className="text-[10px] text-green-500 uppercase font-bold tracking-wider">System Operational</div>
                  </div>
                </div>

                <div className="space-y-3">
                  {[1, 2, 3].map((i) => (
                    <div
                      key={i}
                      className="flex items-center justify-between p-3 bg-zinc-900/50 rounded border border-zinc-800"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded bg-zinc-800 flex items-center justify-center text-zinc-500">
                          <Shield className="w-4 h-4" />
                        </div>
                        <div>
                          <div className="text-xs font-bold text-zinc-300">Policy Check #{100 + i}</div>
                          <div className="text-[10px] text-zinc-600">Encrypted at rest (AES-256)</div>
                        </div>
                      </div>
                      <div className="text-green-500">
                        <CheckItem>{""}</CheckItem>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="absolute inset-0 bg-[linear-gradient(rgba(255,255,255,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.02)_1px,transparent_1px)] bg-[size:20px_20px] pointer-events-none"></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CORE PILLARS */}
      <Section className="py-24">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <FeatureBlock
            icon={Key}
            title="Identity & Access"
            description="Enforce security policies with SAML SSO (Okta, Azure AD) and role-based access control."
          >
            <div className="flex items-center gap-2 p-2 bg-zinc-900 rounded border border-zinc-800">
              <div className="w-6 h-6 rounded bg-blue-600 flex items-center justify-center text-white text-[10px] font-bold">
                O
              </div>
              <div className="text-xs text-zinc-300">Okta Integration Active</div>
            </div>
          </FeatureBlock>

          <FeatureBlock
            icon={Eye}
            title="Audit Logs"
            description="Complete visibility. Track every API call, login, and pipeline modification. Export to Splunk or Datadog."
          >
            <div className="space-y-1 font-mono text-[10px] text-zinc-500">
              <div className="flex justify-between">
                <span>user_login</span> <span>10:42am</span>
              </div>
              <div className="flex justify-between text-zinc-600">
                <span>pipeline_edit</span> <span>10:45am</span>
              </div>
              <div className="flex justify-between">
                <span>api_key_revoked</span> <span>11:00am</span>
              </div>
            </div>
          </FeatureBlock>

          <FeatureBlock
            icon={Globe}
            title="Data Sovereignty"
            description="Keep your media compliant. Process data in specific regions (US, EU) to meet legal requirements."
          >
            <div className="grid grid-cols-2 gap-2">
              <div className="px-2 py-1 bg-zinc-900 border border-zinc-800 rounded text-[10px] text-zinc-400 text-center">
                US-EAST-1
              </div>
              <div className="px-2 py-1 bg-zinc-900 border border-zinc-800 rounded text-[10px] text-zinc-400 text-center">
                EU-WEST-1
              </div>
            </div>
          </FeatureBlock>
        </div>
      </Section>

      {/* DOCUMENTATION DOWNLOADS */}
      <Section className="border-t border-zinc-900 bg-zinc-900/20">
        <div className="flex flex-col md:flex-row items-center justify-between mb-12">
          <h2 className="text-2xl font-bold text-white">Security Documentation</h2>
          <Button variant="outline" asLink="mailto:security@vild.ai">
            Contact Security Team
          </Button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            { title: "Security Overview", type: "PDF", size: "2.4 MB" },
            { title: "SOC 2 Type II Report", type: "PDF", size: "Protected" },
            { title: "Data Processing Addendum", type: "PDF", size: "1.1 MB" },
          ].map((doc) => (
            <div
              key={doc.title}
              className="flex items-center justify-between p-4 rounded-lg border border-zinc-800 bg-zinc-950 hover:bg-zinc-900 hover:border-zinc-700 transition-colors cursor-pointer group"
            >
              <div className="flex items-center gap-3">
                <FileCheck className="w-5 h-5 text-zinc-500 group-hover:text-blue-500 transition-colors" />
                <div>
                  <div className="text-sm font-medium text-white">{doc.title}</div>
                  <div className="text-xs text-zinc-500">
                    {doc.type} • {doc.size}
                  </div>
                </div>
              </div>
              <div className="text-zinc-600 group-hover:text-white transition-colors">↓</div>
            </div>
          ))}
        </div>
      </Section>
    </div>
  );
};

